import { useEffect, useState } from "react";
import { useReducedMotion } from "framer-motion";

function getDeviceState() {
  if (typeof window === "undefined" || !window.matchMedia) {
    return {
      isTouchDevice: false,
      isSmallViewport: false,
    };
  }

  return {
    isTouchDevice: window.matchMedia("(hover: none), (pointer: coarse)").matches,
    isSmallViewport: window.matchMedia("(max-width: 767px)").matches,
  };
}

function addMediaListener(query, listener) {
  if (query.addEventListener) {
    query.addEventListener("change", listener);
    return () => query.removeEventListener("change", listener);
  }

  query.addListener(listener);
  return () => query.removeListener(listener);
}

export default function useAnimationSettings() {
  const prefersReducedMotion = useReducedMotion();
  const [deviceState, setDeviceState] = useState(getDeviceState);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const touchQuery = window.matchMedia("(hover: none), (pointer: coarse)");
    const viewportQuery = window.matchMedia("(max-width: 767px)");

    const updateDeviceState = () => {
      setDeviceState({
        isTouchDevice: touchQuery.matches,
        isSmallViewport: viewportQuery.matches,
      });
    };

    updateDeviceState();

    const removeTouchListener = addMediaListener(touchQuery, updateDeviceState);
    const removeViewportListener = addMediaListener(
      viewportQuery,
      updateDeviceState,
    );

    return () => {
      removeTouchListener();
      removeViewportListener();
    };
  }, []);

  const compactMotion = prefersReducedMotion || deviceState.isSmallViewport;

  return {
    reduceMotion: prefersReducedMotion,
    compactMotion,
    allowHoverAnimations: !prefersReducedMotion && !deviceState.isTouchDevice,
    allowAmbientAnimations:
      !prefersReducedMotion &&
      !deviceState.isTouchDevice &&
      !deviceState.isSmallViewport,
    viewport: { once: true, amount: compactMotion ? 0.18 : 0.28 },
    durations: {
      reveal: compactMotion ? 0.42 : 0.6,
      hero: compactMotion ? 0.5 : 0.7,
      hover: compactMotion ? 0.2 : 0.25,
      stagger: compactMotion ? 0.04 : 0.06,
    },
    offsets: {
      revealY: compactMotion ? 12 : 20,
      subtleY: compactMotion ? 8 : 10,
      sideX: compactMotion ? 10 : 20,
    },
  };
}
